import { getRoleRangesCm, sliceOlLineByCm } from "../../util";

// Annotates each road/lane line feature with `crosslinkUncoveredGeoms` — the
// sub-sections (sliced geometries) NOT covered by any crosslink relation. The
// crosslink style overlays those in a lighter colour.
//
// A member is covered along the LR range(s) its role encodes; a plain role
// (no `=`) means the whole way is covered. The covered ranges from every
// relation referencing the way are merged and complemented against the way's
// length (`lengthCm`, set by WFSLayer) to get the uncovered sub-sections.
//
// Recomputed on every featuresloadend because a way and the relations covering
// it can arrive in separate bbox loads. The handler only sets a property (no
// addFeature), so it doesn't re-fire featuresloadend.
export const crosslinkCoverageHandler = (event) => {
  const features = event.target.getFeatures();
  const featureById = new Map(features.map((f) => [f.getId(), f]));
  // memberId -> array of covered ranges; a `null` entry means "fully covered".
  const coverageById = new Map();
  // memberId -> ranges to draw red: the matching segment on THIS member when
  // the OTHER member of a shared relation overshoots its own length.
  const redRangesById = new Map();
  features.forEach((relation) => {
    if (relation.get("type") !== "ORBIS_RELATION") {
      return;
    }
    relation.getKeys().forEach((key) => {
      if (!(key.startsWith("relation-member-") && key.endsWith("-id"))) {
        return;
      }
      const memberId = relation.get(key);
      const role = relation.get(key.replace("-id", "-role"));
      const ranges = getRoleRangesCm(role);
      const list = coverageById.get(memberId) || [];
      if (typeof role === "string" && role.includes("=") && ranges.length > 0) {
        list.push(...ranges);
      } else {
        // Plain role (full way) or unparseable LR → treat as fully covered.
        list.push(null);
      }
      coverageById.set(memberId, list);
    });

    // When a member's role runs past that member's own length the relation is
    // out-of-bounds (a data error). The user wants the error shown on the
    // PARTNER: road overshoots → flag the matching lane segment red, and vice
    // versa. For each range index where member A overshoots, mark every other
    // member B's matching section (paired by index, like the hover handler).
    const members = relation
      .getKeys()
      .filter(
        (key) => key.startsWith("relation-member-") && key.endsWith("-id"),
      )
      .map((key) => {
        const id = relation.get(key);
        return {
          id,
          ranges: getRoleRangesCm(relation.get(key.replace("-id", "-role"))),
          feature: featureById.get(id),
        };
      });
    members.forEach((a) => {
      const lengthCm = a.feature && a.feature.get("lengthCm");
      if (!(lengthCm > 0)) {
        return;
      }
      a.ranges.forEach((range, index) => {
        if (!(Number.isFinite(range.endCm) && range.endCm > lengthCm)) {
          return;
        }
        members.forEach((b) => {
          if (b.id === a.id) {
            return;
          }
          const list = redRangesById.get(b.id) || [];
          list.push(...matchingRanges(b.ranges, index));
          redRangesById.set(b.id, list);
        });
      });
    });
  });

  features.forEach((feature) => {
    if (feature.get("type") === "ORBIS_RELATION") {
      return;
    }
    const uncovered = uncoveredRangesCm(
      coverageById.get(feature.getId()),
      feature.get("lengthCm"),
    );
    const geometries = uncovered.map((range) =>
      sliceOlLineByCm(feature.getGeometry(), range.startCm, range.endCm),
    );
    feature.set("crosslinkUncoveredGeoms", geometries);

    const redRanges = redRangesById.get(feature.getId()) || [];
    const outOfBoundsGeoms = redRanges.map((range) =>
      sliceOlLineByCm(feature.getGeometry(), range.startCm, range.endCm),
    );
    feature.set("crosslinkOutOfBoundsGeoms", outOfBoundsGeoms);
  });
};

// The section(s) on one member that pair with range `index` of the partner —
// same index-based pairing the hover handler (CrosslinkLayerHandler) uses. A
// plain role (no decodable ranges) covers the whole way: [0, Infinity], which
// sliceOlLineByCm clamps to the geometry length.
function matchingRanges(ranges, index) {
  if (ranges.length === 0) {
    return [{ startCm: 0, endCm: Infinity }];
  }
  if (ranges.length === 1) {
    return [ranges[0]];
  }
  if (index < ranges.length) {
    return [ranges[index]];
  }
  return ranges; // counts don't line up → highlight all the partner's sections
}

// The complement of the covered ranges over [0, lengthCm].
function uncoveredRangesCm(coverage, lengthCm) {
  const total = lengthCm > 0 ? lengthCm : Infinity;
  if (coverage === undefined) {
    return [{ startCm: 0, endCm: total }]; // referenced by nothing → all uncovered
  }
  if (coverage.some((range) => range === null)) {
    return []; // fully covered
  }
  const merged = [];
  coverage
    .map((range) => ({
      startCm: Math.max(0, range.startCm),
      endCm: Math.min(range.endCm, total),
    }))
    .filter((range) => range.endCm > range.startCm)
    .sort((a, b) => a.startCm - b.startCm)
    .forEach((range) => {
      const last = merged[merged.length - 1];
      if (last && range.startCm <= last.endCm) {
        last.endCm = Math.max(last.endCm, range.endCm);
      } else {
        merged.push({ ...range });
      }
    });

  const gaps = [];
  let cursor = 0;
  merged.forEach((range) => {
    if (range.startCm > cursor) {
      gaps.push({ startCm: cursor, endCm: range.startCm });
    }
    cursor = Math.max(cursor, range.endCm);
  });
  if (cursor < total) {
    gaps.push({ startCm: cursor, endCm: total });
  }
  return gaps;
}
